import React, { useEffect, useRef, useState } from 'react';
import { TRANSFORMERS } from '@lexical/markdown';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { CLEAR_HISTORY_COMMAND } from 'lexical';

import AutoLinkPlugin from '../plugins/AutoLinkPlugin';
import ToolbarPlugin from '../plugins/ToolbarPlugin';
import ImagePlugin from '../plugins/ImagePlugin';

import DragDropPastePlugin from '../plugins/DragDropPastePlugin';
import InitialEditorStatePlugin from '../plugins/InitialEditorStatePlugin';
import FloatingLinkEditorPlugin from '../plugins/FloatingLinkEditorPlugin';
import HorizontalRulePlugin from '../plugins/HorizontalRulePlugin';

import CollapsiblePlugin from '../plugins/CollapsiblePlugin';
import CollapsibleDeletePlugin from '../plugins/CollapsibleDeletePlugin';
import HtmlPlugin from '../plugins/HtmlPlugin';
import ParagraphHygienePlugin, { healNestedBlocks } from '../plugins/ParagraphHygienePlugin';
import LineBreakShortcutPlugin from '../plugins/LineBreakShortcutPlugin';
import { AutoFocusPlugin } from '../plugins/AutoFocusPlugin';
import DraggableBlockPlugin from '../plugins/DraggableBlockPlugin';
import SlashCommandPlugin from '../plugins/SlashCommandPlugin';
import BlockPlaceholderPlugin from '../plugins/BlockPlaceholderPlugin';

import BaseEditor from './BaseEditor';

const EXTERNAL_VALUE_TAG = 'external-value';

function isLexicalJson(value) {
  if (typeof value !== 'string' || value === '') return false;
  try {
    const parsed = JSON.parse(value);
    return Boolean(parsed && parsed.root && Array.isArray(parsed.root.children));
  } catch {
    return false;
  }
}

function Placeholder({ placeholder }) {
  return (
    <div className="overflow-hidden absolute truncate top-[17px] text-sm left-8 select-none inline-block pointer-events-none text-gray-500 dark:text-muted-foreground">
      {placeholder}
    </div>
  );
}

function RichTextEditor({ onChange, value, placeholder, onLoading, autoFocus = true }) {
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);
  const [editor] = useLexicalComposerContext();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    onLoading(isLoading);
  }, [isLoading]);

  // The form can hand back a value the editor didn't produce (the API normalises
  // on save). Adopt it without re-emitting, or the form turns dirty again.
  const lastEmittedValue = useRef(value);

  const handleChange = (editorState, _editor, tags) => {
    if (tags.has(EXTERNAL_VALUE_TAG)) return;
    const nextValue = JSON.stringify(editorState.toJSON());
    lastEmittedValue.current = nextValue;
    onChange(nextValue);
  };

  useEffect(() => {
    if (value === lastEmittedValue.current) return;
    lastEmittedValue.current = value;
    if (!isLexicalJson(value)) return;
    editor.setEditorState(editor.parseEditorState(value), { tag: EXTERNAL_VALUE_TAG });
    healNestedBlocks(editor);
    editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
  }, [editor, value]);

  // we need an element reference for the container element that
  // any floating elements in plugins will be rendered inside
  const [floatingAnchorElem, setFloatingAnchorElem] = React.useState(null);
  const onContentEditableRef = _floatingAnchorElem => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  return (
    <>
      <div className="editor-container max-w-[488px] rounded-sm text-black dark:text-foreground relative leading-5 text-left">
        <div className="sticky top-16 z-10 ml-[26px]">
          <ToolbarPlugin setIsLinkEditMode={setIsLinkEditMode} setIsLoading={setIsLoading} />
        </div>

        <div className="editor-inner relative border-t border-b border-gray-200 dark:border-border mb-6">
          <InitialEditorStatePlugin initialValue={value} />

          <RichTextPlugin
            contentEditable={
              <div ref={onContentEditableRef} className="relative">
                <ContentEditable className="editor-input min-h-[150px] resize-none caret-black relative outline-none pt-4 pr-2 pb-4 pl-8" />
              </div>
            }
            placeholder={<Placeholder placeholder={placeholder} />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <DragDropPastePlugin setIsLoading={setIsLoading} />
          <ImagePlugin />
          <HistoryPlugin />

          <ListPlugin />
          <LinkPlugin />

          {floatingAnchorElem !== null && (
            <CollapsibleDeletePlugin anchorElem={floatingAnchorElem} />
          )}

          {floatingAnchorElem !== null && (
            <>
              <FloatingLinkEditorPlugin
                anchorElem={floatingAnchorElem}
                isLinkEditMode={isLinkEditMode}
                setIsLinkEditMode={setIsLinkEditMode}
              />
              <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
            </>
          )}

          <HorizontalRulePlugin />
          <CollapsiblePlugin />
          <HtmlPlugin />
          <ParagraphHygienePlugin />
          <LineBreakShortcutPlugin />

          {/* <ClickableLinkPlugin /> */}
          <AutoLinkPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <OnChangePlugin onChange={handleChange} />
          {/* Focusing scrolls the editor into view, so an editor that is not the
              first thing on screen drags the page to itself as it mounts. */}
          {autoFocus && <AutoFocusPlugin />}
          <SlashCommandPlugin />
          <BlockPlaceholderPlugin />
        </div>
      </div>
    </>
  );
}

export default function RichTextEditorWrapper({
  value,
  placeholder,
  onChange,
  onLoading,
  autoFocus,
}) {
  return (
    <BaseEditor value={value}>
      <RichTextEditor
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onLoading={onLoading}
        autoFocus={autoFocus}
      />
    </BaseEditor>
  );
}
